import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useStatus } from '../context/StatusContext';
import { useDataUpdates } from '../hooks/useDataUpdates';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../styles/Wallets.css';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { StatusEvaluator } from '../context/StatusEvaluator';
import LoadingAnimation from '../components/loadingAnimation';
import { BALANCE_THRESHOLDS } from '../config/thresholds';
import { getApiBase } from '../utils/apiBase';

const AddressBalances = () => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { updatePageStatus } = useStatus();
  const isInitialLoadRef = useRef(true);

  const defaultColDef = useMemo(() => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressMovable: true,
  }), []);

  
    const columnDefs = useMemo(() => [
        { 
          field: 'network',
          headerName: 'Network',
          width: 120,
          flex: 1,
          sort: 'asc',
          headerClass: 'ag-header-cell-label',
        },
        { 
          field: 'role',
          headerName: 'Role',
          width: 180,
          flex: 1,
          sort: 'asc',
          headerClass: 'ag-header-cell-label',
        },
        { 
          field: 'address',
          headerName: 'Address',
          width: 450,
          flex: 1.5,
          headerClass: 'ag-header-cell-label',
        },
        { 
          field: 'flareBalance',
          headerName: 'Flare Balance',
          width: 200,
          flex: 1,
          headerClass: 'ag-header-cell-label',
          type: 'numericColumn',
          cellRenderer: params => {
            if (!params.value) return '';
            const formattedValue = Number(params.value).toFixed(4);
            return (
              <div className="balance-container">
                <span>{formattedValue}</span>
                <button 
                  className="explorer-link-button"
                  onClick={() => window.open(
                    `https://flare-explorer.flare.network/address/${params.data.address}`,
                    '_blank'
                  )}
                >
                  <FaExternalLinkAlt size={14} />
                </button>
              </div>
            );
          },
          cellClass: params => {
            if (!params.value) return ['balance-cell'];
            const balance = Number(params.value);
            let classes = ['balance-cell'];
            if (balance < BALANCE_THRESHOLDS.RED_BELOW) {
              classes.push('balance-red');
            } else if (balance < BALANCE_THRESHOLDS.AMBER_BELOW) {
              classes.push('balance-amber');
            } else {
              classes.push('balance-ok');
            }
            return classes;
          }
        },
        { 
          field: 'songbirdBalance',
          headerName: 'Songbird Balance',
          width: 200,
          flex: 1,
          headerClass: 'ag-header-cell-label',
          type: 'numericColumn',
          cellRenderer: params => {
            if (!params.value) return '';
            const formattedValue = Number(params.value).toFixed(4);
            return (
              <div className="balance-container">
                <span>{formattedValue}</span>
                <button 
                  className="explorer-link-button"
                  onClick={() => window.open(
                    `https://songbird-explorer.flare.network/address/${params.data.address}`,
                    '_blank'
                  )}
                >
                  <FaExternalLinkAlt size={14} />
                </button>
              </div>
            );
          },
          cellClass: params => {
            if (!params.value) return ['balance-cell'];
            const balance = Number(params.value);
            let classes = ['balance-cell'];
            if (balance < BALANCE_THRESHOLDS.RED_BELOW) {
              classes.push('balance-red');
            } else if (balance < BALANCE_THRESHOLDS.AMBER_BELOW) {
              classes.push('balance-amber');
            } else {
              classes.push('balance-ok');
            }
            return classes;
          }
        },
    ], []);

    const handleDataUpdate = useCallback(async () => {
      if (isInitialLoadRef.current) setLoading(true);
      setError(null);
      try {
        const API = getApiBase();
        const response = await fetch(`${API}/address_balances.json`, { cache: 'no-store' });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        const rows = [];
        
        Object.entries(data).forEach(([network, networkData]) => {
          const flareData = networkData.flare;
          const songbirdData = networkData.songbird;
          
          Object.entries(flareData).forEach(([role, flareDetails]) => {
            if (role === 'Delegation') {
              rows.push({
                network,
                role,
                address: flareDetails.address,
                flareBalance: flareDetails.balance,
                songbirdBalance: null
              });
              rows.push({
                network,
                role,
                address: songbirdData[role].address,
                flareBalance: null,
                songbirdBalance: songbirdData[role].balance
              });
            } else {
              rows.push({
                network,
                role,
                address: flareDetails.address,
                flareBalance: flareDetails.balance,
                songbirdBalance: songbirdData[role].balance
              });
            }
          });
        });
        
        setRowData(rows);
        
        const status = StatusEvaluator.wallets(rows);
        updatePageStatus('balances', status);
      } catch (error) {
        console.error('Error fetching wallet data:', error);
        setError(error.message);
        updatePageStatus('balances', 'red');
      }
      finally {
        if (isInitialLoadRef.current) setLoading(false);
        isInitialLoadRef.current = false;
      }
    }, [updatePageStatus]);

    useDataUpdates(handleDataUpdate);

    useEffect(() => {
      handleDataUpdate();
      return () => setRowData([]);
    }, [handleDataUpdate]);

  if (loading) {
    return <LoadingAnimation />;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  if (rowData.length === 0 && !loading && !error) {
    return null;
  }

  return (
    <div className="main-wrapper">
      <div className="wallets-wrapper">
        <div className="wallets-grid-wrapper">
          <div className="ag-theme-alpine-dark custom-dark-theme">
            <AgGridReact
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressBalances;